// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { type ZodEffects, ZodError, type ZodIssue, type ZodInvalidTypeIssue } from 'zod';
import { windowState } from '@/js/WindowState';
import { formDataToObj } from '@/js/utils/normalizer';

export type SwitchItem = {
  name: string;
  schemaByValue: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: ZodEffects<any>;
  };
};

export type ZodState = {
  dirty: { [path: string]: boolean };
};

export type ValidatorState = {
  ready: boolean;
  submitted: boolean;
  errors: (false | ZodInvalidTypeIssue | undefined)[];
} & ZodState;

export type StateCallback = (state: ValidatorState) => void;
export type StateWithEventCallback = (state: ValidatorState, event?: Event) => void;

export type ValidatorOptions = {
  realtime?: boolean;
  switchable?: SwitchItem[];
  errorToScrollSelector: string;
  on: {
    validate?: StateWithEventCallback;
    ready?: StateCallback;
  };
};

export type ValidatorConfig = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: ZodEffects<any>;
  options?: ValidatorOptions;
};

export type ValidatorInstance = {
  schema: ValidatorConfig['schema'];
  formData: FormData;
  error: null | ZodError;
  dirty: boolean;
  options: ValidatorOptions;
  state: ZodState;
};

export default class Validator {
  el: HTMLFormElement;
  instance: null | ValidatorInstance;
  config: ValidatorConfig;
  options: ValidatorOptions;
  state: ValidatorState;

  constructor(selector: string, config: ValidatorConfig) {
    this.el = document.querySelector(selector) as HTMLFormElement;
    this.instance = null;
    this.config = config;
    this.options = {
      realtime: false,
      switchable: [],
      errorToScrollSelector: 'body',
      ...config.options,
      on: {
        ...config.options?.on,
      },
    };
    this.state = {
      dirty: {},
      ready: false,
      submitted: false,
      errors: [],
    };

    if (this.el) {
      this.bind();
      this.initialize();
    }
  }

  private bind() {
    this.initialize = this.initialize.bind(this);
    this.updateSchema = this.updateSchema.bind(this);
    this.validate = this.validate.bind(this);
    this.onChangeField = this.onChangeField.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  /**
   * 初期化
   */
  private async initialize() {
    const validator = this.createValidator(this.el);

    this.el.addEventListener('change', this.onChangeField);
    this.el.addEventListener('submit', this.onSubmit);

    this.el.$validator = validator;

    this.state.errors = await this.validate(this.el);

    this.instance = validator;

    this.state.ready = true;
    if (this.options.on.ready) {
      this.options.on.ready({ ...this.state });
    }
  }

  /**
   * バリデーターオブジェクトを生成
   */
  private createValidator(form: HTMLFormElement): ValidatorInstance {
    const fields = form.querySelectorAll('[data-field] :is(input, textarea, select)') as NodeListOf<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >;
    const formData = new FormData();
    const validator = {
      formData,
      schema: this.config.schema,
      error: null,
      dirty: false,
      options: this.options,
      state: {
        dirty: {},
      },
    };

    if (!validator.schema) {
      throw new Error('schemaが指定されていません');
    }

    fields.forEach((field) => {
      // let fieldValue = field.value;

      // if (this.isSelective(field.type)) {
      //   fieldValue = (field as HTMLInputElement).checked ? fieldValue : '';
      // }

      if (this.isCheckbox(field.type)) {
        // (field as HTMLInputElement).checked = false;
        // field.value = false
        // console.log('field.value', field.value);
        // (field as HTMLInputElement).addEventListener('change', (e) => {
        //   const target = e.currentTarget as HTMLInputElement;
        //   // target?.setAttribute('data-changed', '')
        //   target.checked = true;
        //   target.checked = false;
        // }, { once: true })
      }

      // console.log('初期データ', { name: field.name, value: field.value })

      formData.append(field.name, field.value);
      validator.state.dirty = { ...validator.state.dirty, ...{ [field.name]: false } };
    });
    // // @ts-ignore
    // window.DebugFormData = formData
    // console.log('validator', validator)
    return validator;
  }

  /**
   * フィールド変更時の処理
   */
  private async onChangeField(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const validator = target?.form?.$validator as ValidatorInstance;

    const { formData, options, dirty } = validator;

    if (target.dataset && Object.prototype.hasOwnProperty.call(target.dataset, 'validateIgnore')) {
      return;
    }

    if (!dirty) {
      validator.dirty = true;
    }

    if (target.type === 'checkbox') {
      formData.delete(target.name);
      // @ts-ignore
      formData.append(target.name, target.checked ? 'true' : '');
      // const values = formData.getAll(target.name);
      // formData.delete(target.name);
      // // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // // @ts-ignore
      // const newValues = target.checked ? [...values, target.value] : values.filter((value) => value !== target.value);
      // newValues.forEach((value) => {
      //   formData.append(target.name, value);
      // });

      // const values = formData.getAll(target.name);
      // formData.delete(target.name);
      // // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // // @ts-ignore
      // const newValues = target.checked ? [...values, target.value] : values.filter((value) => value !== target.value);
      // newValues.forEach((value) => {
      //   formData.append(target.name, value);
      // });
    } else {
      let newValue = target.value;

      if (target.type === 'radio') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        newValue = target.checked ? target.value : '';
      }
      formData.set(target.name, newValue);
    }

    if (!target.form) {
      throw new Error('formが存在しません');
    }

    if (options.realtime) {
      await this.validate(target.form, event);
    }
  }

  /**
   * 送信時の処理
   */
  private async onSubmit(event: Event) {
    event.preventDefault();

    const target = event.target as HTMLFormElement;
    const { error, dirty } = target.$validator as ValidatorInstance;

    if (!this.state.submitted) {
      this.state.submitted = true;
    }

    if (!dirty) {
      target.$validator.dirty = true;
    }

    await this.validate(target, event);

    if (error && error?.issues.length) {
      // NOTE: app instanceの相互importによるmoduleアクセスは良くないので、変えておく
      // app.modules.smoothScroll.scroller.to(this.options.errorToScrollSelector);
      // return;
    }

    target.submit();
    return;
  }

  private isCheckbox(type: string) {
    return /checkbox/.test(type);
  }

  private isSelective(type: string) {
    return /checkbox|radio/.test(type);
  }

  private getValidator(form: HTMLFormElement): ValidatorInstance {
    if (!form || !form.$validator) {
      throw new Error('formが未指定か、validatorが初期化されていません');
    }
    return form.$validator;
  }

  /**
   * スキーマパターンを更新
   */
  async updateSchema(form: HTMLFormElement, newSchema: ZodEffects<never>) {
    const validator = this.getValidator(form);
    validator.schema = newSchema;
    await this.validate(form);
  }

  /**
   * データのdirtyフラグを更新
   */
  updateDirty(validator: ValidatorInstance, formData: FormData, forceTrue?: boolean) {
    const data = formDataToObj(formData) as { [name: string]: string };
    Object.keys(data).forEach((key) => {
      const isDirty = validator.state.dirty[key];

      if (data[key] && (forceTrue || !isDirty)) {
        validator.state.dirty[key] = true;
      }
    });
  }

  /**
   * バリデーション処理
   */
  async validate(form: HTMLFormElement, event?: Event) {
    let hasError = false;
    const validator = this.getValidator(form);
    const { formData, schema } = validator;
    const isSubmitEvent = event?.type === 'submit';

    this.updateDirty(validator, formData);

    try {
      await schema.parse(formData);
    } catch (error) {
      if (error instanceof ZodError) {
        validator.error = error;
      }
      hasError = true;
    }

    if (!hasError) {
      validator.error = null;
    }

    const zodError = validator.error;
    const fields = [...document.querySelectorAll('[data-field-id]:has([data-field])')] as HTMLElement[];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errors = fields
      .map((field) => {
        const fieldTargets = [...field.querySelectorAll('[data-field] :is(input, textarea, select)')];
        const errorField = field.querySelector('[data-field-error]') as HTMLElement;
        const fieldName = field.dataset.fieldId || '';
        const regExp = new RegExp(`^(${fieldName.replace(/\[\]/g, '\\[\\]')})$`);
        const fieldError = zodError?.issues?.find((err) => {
          const errPath = err.path[0];
          return regExp.test(`${errPath}`);
        }) as ZodInvalidTypeIssue | undefined;

        // エラーがなければ、エラー状態を解除
        if (!fieldError) {
          field.classList.remove('has-error');
          fieldTargets.forEach((it) => {
            it.setAttribute('aria-invalid', 'false');
          });
          return;
        }

        const isDirty = fieldError ? validator.state.dirty[fieldError.path[0]] : false;

        if (isDirty || isSubmitEvent) {
          field.classList.add('has-error');
          fieldTargets.forEach((it) => {
            it.setAttribute('aria-invalid', 'true');
          });
        }

        if (errorField) {
          if (this.state.ready || isSubmitEvent) {
            errorField.innerText = fieldError?.message || '';
          } else {
            errorField.innerText = '';
          }
        }

        return fieldError;
      })
      .filter(Boolean);

    this.state.errors = errors;

    if (this.options.on.validate) {
      this.options.on.validate({ ...this.state }, event);
    }
    return errors;
  }
}
